import { Box, Container, Typography, Grid } from "@mui/material";
import React from "react";
import FaqSection from "../component/about/FaqSection";
import OfferSection from "../component/about/OfferSection";
import DesSec from "../component/Home/DesSec";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";

const pillars = [
  {
    icon: <EmojiObjectsIcon sx={{ fontSize: 28 }} />,
    bg: "linear-gradient(135deg,#4F6EF7 0%,#7C3AED 100%)",
    glow: "rgba(79,110,247,0.3)",
    title: "Innovation First",
    desc: "We continuously evolve with AI and technology to stay ahead of educational needs.",
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 28 }} />,
    bg: "linear-gradient(135deg,#10B981 0%,#0EA5E9 100%)",
    glow: "rgba(16,185,129,0.3)",
    title: "Measurable Impact",
    desc: "Every feature is designed to deliver tangible outcomes for schools, parents, and students.",
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 28 }} />,
    bg: "linear-gradient(135deg,#F59E0B 0%,#EF4444 100%)",
    glow: "rgba(245,158,11,0.3)",
    title: "Trust & Security",
    desc: "Enterprise-grade security and privacy to keep your school's data safe at all times.",
  },
];

const About = () => {
  return (
    <Box>
      {/* ===== HERO ===== */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 10, md: 14 },
          pb: { xs: 10, md: 14 },
          overflow: "hidden",
          background: "linear-gradient(160deg, #F8FAFF 0%, #EEF2FF 60%, #F0F7FF 100%)",
        }}
      >
        {/* Decorative blobs */}
        <Box sx={{ position:"absolute", top:-100, right:-100, width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,rgba(79,110,247,0.1) 0%,transparent 70%)", pointerEvents:"none" }} />
        <Box sx={{ position:"absolute", bottom:-60, left:-60, width:340, height:340, borderRadius:"50%", background:"radial-gradient(circle,rgba(124,58,237,0.07) 0%,transparent 70%)", pointerEvents:"none" }} />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.8,
              borderRadius: "100px",
              bgcolor: "rgba(79,110,247,0.08)",
              border: "1px solid rgba(79,110,247,0.2)",
              mb: 3,
            }}
          >
            <Typography fontSize="0.82rem" fontWeight={600} color="#4F6EF7" letterSpacing="0.04em">
              ABOUT US
            </Typography>
          </Box>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.2rem", sm: "2.8rem", md: "3.6rem" },
              fontWeight: 900,
              color: "#0D1B4B",
              mb: 3,
            }}
          >
            Reimagining School Management
            <br />
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #4F6EF7 0%, #7C3AED 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              for the Digital Era
            </Box>
          </Typography>

          <Typography
            variant="h6"
            color="#64748B"
            sx={{ maxWidth: 700, mx: "auto", lineHeight: 1.8, fontWeight: 400 }}
          >
            MySchoolling is a comprehensive digital ecosystem built to empower
            schools, streamline operations, and enhance student learning through
            intelligent technology and seamless communication.
          </Typography>
        </Container>
      </Box>

      {/* ===== SPLIT SECTION ===== */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: "#fff" }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 10 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.8,
                  borderRadius: "100px",
                  bgcolor: "rgba(79,110,247,0.08)",
                  border: "1px solid rgba(79,110,247,0.2)",
                  mb: 3,
                }}
              >
                <Typography fontSize="0.82rem" fontWeight={600} color="#4F6EF7" letterSpacing="0.04em">
                  OUR STORY
                </Typography>
              </Box>

              <Typography variant="h3" fontWeight={800} color="#0D1B4B" gutterBottom sx={{ mb: 3 }}>
                Built with Intelligence. Designed for Impact.
              </Typography>

              <Typography variant="body1" color="#475569" sx={{ lineHeight: 1.9, mb: 2 }}>
                Education is evolving rapidly, and institutions require modern
                solutions that adapt to new challenges. MySchoolling combines
                AI-powered learning tools, real-time communication, and
                comprehensive administrative management into one seamless platform.
              </Typography>

              <Typography variant="body1" color="#475569" sx={{ lineHeight: 1.9 }}>
                From academic tracking and performance analytics to transport
                monitoring and parent engagement, we provide schools with clarity,
                control, and confidence to operate efficiently in a connected world.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    inset: "10%",
                    borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(79,110,247,0.1) 0%, transparent 70%)",
                    filter: "blur(40px)",
                  }}
                />
                <Box
                  component="img"
                  src="/about.png"
                  alt="MySchoolling Platform"
                  sx={{
                    width: "100%",
                    borderRadius: "24px",
                    boxShadow: "0 40px 100px rgba(13,27,75,0.12)",
                    border: "1px solid rgba(255,255,255,0.8)",
                    position: "relative",
                    zIndex: 1,
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Three pillars */}
          <Grid container spacing={3} mt={{ xs: 8, md: 10 }}>
            {pillars.map((p, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box
                  sx={{
                    p: 4,
                    borderRadius: "20px",
                    border: "1px solid rgba(13,27,75,0.07)",
                    bgcolor: "#F8FAFF",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: `0 20px 60px ${p.glow}`,
                      borderColor: "rgba(79,110,247,0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      background: p.bg,
                      color: "#fff",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2.5,
                      boxShadow: `0 8px 20px ${p.glow}`,
                    }}
                  >
                    {p.icon}
                  </Box>
                  <Typography fontWeight={700} color="#0D1B4B" mb={1} fontSize="1.05rem">
                    {p.title}
                  </Typography>
                  <Typography variant="body2" color="#64748B" lineHeight={1.75}>
                    {p.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ===== VISION BLOCK ===== */}
      <Box sx={{ py: { xs: 10, md: 12 }, bgcolor: "#F8FAFF" }}>
        <Container maxWidth="md">
          <Box
            sx={{
              p: { xs: 5, md: 8 },
              borderRadius: "28px",
              background: "linear-gradient(135deg, #0D1B4B 0%, #1A2F6E 100%)",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box sx={{ position:"absolute", top:-60, right:-60, width:240, height:240, borderRadius:"50%", background:"rgba(79,110,247,0.15)", pointerEvents:"none" }} />
            <Box sx={{ position:"absolute", bottom:-40, left:-40, width:160, height:160, borderRadius:"50%", background:"rgba(255,255,255,0.04)", pointerEvents:"none" }} />

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h3"
                fontWeight={800}
                color="#fff"
                gutterBottom
                sx={{ mb: 3, fontSize: { xs: "1.8rem", md: "2.4rem" } }}
              >
                Our Vision
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  lineHeight: 1.9,
                  fontSize: "1.05rem",
                  maxWidth: 700,
                  mx: "auto",
                }}
              >
                We envision a future where schools operate seamlessly,
                parents stay connected, and students receive personalized
                academic support powered by intelligent insights.
                <br /><br />
                By integrating advanced technology with thoughtful design,
                MySchoolling delivers an enterprise-grade platform that
                supports institutional growth and measurable academic success.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* OFFER SECTION */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: "#fff" }}>
        <OfferSection />
      </Box>

      {/* STATS */}
      <DesSec />

      {/* FAQ */}
      <Box id="faq" sx={{ py: { xs: 10, md: 14 }, bgcolor: "#F8FAFF" }}>
        <FaqSection />
      </Box>
    </Box>
  );
};

export default About;
